import { useTranslation } from 'react-i18next';
import type { ProtocolRow } from '@/types/api';
import { describeEntry } from '@/utils/dental';
import { formatDate, formatTime } from '../chart/components';
import { patientFullName } from '../consents/patientLines';

/**
 * The protocol's columns (Protokol bolesnika, #23): number, date and time, the
 * patient (name with one parent's, JMBG or LBO, birth and sex, address and
 * phone), anamnesis, MKB-10 diagnoses, the work done and the doctor with their
 * ID number. The same table on screen and on paper.
 */
export function ProtocolTable({
  rows,
  onPatient,
}: {
  rows: ProtocolRow[];
  onPatient?: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <table className="protocol-table">
      <thead>
        <tr>
          <th scope="col">{t('protocol.col.number')}</th>
          <th scope="col">{t('protocol.col.when')}</th>
          <th scope="col">{t('protocol.col.patient')}</th>
          <th scope="col">{t('protocol.col.id')}</th>
          <th scope="col">{t('protocol.col.born')}</th>
          <th scope="col">{t('protocol.col.contact')}</th>
          <th scope="col">{t('protocol.col.anamnesis')}</th>
          <th scope="col">{t('protocol.col.diagnosis')}</th>
          <th scope="col">{t('protocol.col.services')}</th>
          <th scope="col">{t('protocol.col.doctor')}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const p = r.patient;
          const name = patientFullName(p);
          return (
            <tr key={r.visitId}>
              <td className="protocol-table__num">{r.number}</td>
              <td className="protocol-table__nowrap">
                {formatDate(r.startedAt)}
                <br />
                {formatTime(r.startedAt)}
              </td>
              <td>
                {onPatient ? (
                  <button type="button" className="link" onClick={() => onPatient(p.id)}>
                    {name}
                  </button>
                ) : (
                  name
                )}
              </td>
              <td className="protocol-table__nowrap">
                {p.jmbg && <div>{t('protocol.jmbg', { value: p.jmbg })}</div>}
                {p.lbo && <div>{t('protocol.lbo', { value: p.lbo })}</div>}
                {!(p.jmbg || p.lbo) && <span className="protocol-table__missing">—</span>}
              </td>
              <td className="protocol-table__nowrap">
                {p.birthDate ? formatDate(`${p.birthDate}T12:00:00`) : '—'}
                {p.sex && (
                  <>
                    <br />
                    {t(`patients.sex.${p.sex}`)}
                  </>
                )}
              </td>
              <td>
                {p.address ?? '—'}
                {p.phone && (
                  <>
                    <br />
                    {p.phone}
                  </>
                )}
              </td>
              <td className="protocol-table__text">{r.anamnesis ?? ''}</td>
              <td>
                {r.diagnoses.map((d) => (
                  <div key={d.code}>
                    <strong>{d.code}</strong> {d.name}
                  </div>
                ))}
              </td>
              <td>
                {r.services.map((s, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: the work of one visit, in recorded order
                  <div key={i}>{describeEntry(s)}</div>
                ))}
              </td>
              <td>
                {r.dentist?.name ?? '—'}
                {r.dentist?.licenseNumber && (
                  <>
                    <br />
                    {t('protocol.license', { value: r.dentist.licenseNumber })}
                  </>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
